'use client';

import { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

// Using Share2 for Twitter/X since lucide-react uses this as the generic social icon

interface SocialShareButtonsProps {
  shareText: string;
  shareUrl: string;
  variant?: 'inline' | 'card';
  eventName?: string;
}

export function SocialShareButtons({
  shareText,
  shareUrl,
  variant = 'inline',
  eventName,
}: SocialShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = (platform: 'twitter' | 'linkedin' | 'copy') => {
    // Track share event
    if (eventName) {
      trackEvent({
        event: eventName,
        metadata: { platform, url: shareUrl },
      });
    }

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          '_blank',
          'noopener,noreferrer'
        );
        break;
      case 'copy':
        const fullText = `${shareText} ${shareUrl}`;
        navigator.clipboard.writeText(fullText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
    }
  };

  const buttons = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleShare('twitter')}
        className="group flex items-center gap-2 rounded-lg border border-neutral-700 bg-slate-50 px-4 py-2.5 text-sm text-muted-foreground transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
        aria-label="Share on Twitter/X"
      >
        <Share2 className="h-4 w-4 transition-colors group-hover:text-primary" />
        <span className="hidden sm:inline">Twitter</span>
      </button>

      <button
        onClick={() => handleShare('linkedin')}
        className="group flex items-center gap-2 rounded-lg border border-neutral-700 bg-slate-50 px-4 py-2.5 text-sm text-muted-foreground transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
        aria-label="Share on LinkedIn"
      >
        <svg
          className="h-4 w-4 transition-colors group-hover:text-primary"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
        <span className="hidden sm:inline">LinkedIn</span>
      </button>

      <button
        onClick={() => handleShare('copy')}
        className="group flex items-center gap-2 rounded-lg border border-neutral-700 bg-slate-50 px-4 py-2.5 text-sm text-muted-foreground transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
        aria-label="Copy link"
      >
        {copied ? (
          <>
            <Check className="h-4 w-4 text-primary" />
            <span className="hidden sm:inline text-primary">Copied!</span>
          </>
        ) : (
          <>
            <Copy className="h-4 w-4 transition-colors group-hover:text-primary" />
            <span className="hidden sm:inline">Copy Link</span>
          </>
        )}
      </button>
    </div>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-slate-50 p-6 backdrop-blur-sm">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Share
        </h3>
        {buttons}
      </div>
    );
  }

  return buttons;
}
